import React, { useMemo, useState } from "react";
import { FiChevronDown, FiColumns, FiLock, FiRefreshCw } from "react-icons/fi";
import { filterColumnGroups, groupVisibility } from "./panelModel";
import { Highlight, SidePanel, ViewsBar } from "./SidePanel";

const SHOW_OPTIONS = [
    { id: "all", label: "Todas" },
    { id: "visible", label: "Visibles" },
    { id: "hidden", label: "Ocultas" },
];

// Panel de columnas de la hoja.
// groups: [{ id, label, icon, columns: [{ id, label, locked }] }]
export default function ColumnsPanel({
    groups, isVisible, onToggle, onSetMany,
    views, activeViewId, onApplyView, onDeleteView, onSaveView, saveStatus, maxNameLength,
    onReset, onShowAll, onClose,
}) {
    const [query, setQuery] = useState("");
    const [show, setShow] = useState("all");
    // Grupos abiertos: el primero (tabla principal) de inicio. Buscando o filtrando
    // por visibilidad se abren todos para ver los resultados.
    const [openGroups, setOpenGroups] = useState(() => new Set(groups.slice(0, 1).map((group) => group.id)));
    const filtering = Boolean(query.trim()) || show !== "all";

    const shownGroups = useMemo(() => filterColumnGroups(groups, { query, show, isVisible }), [groups, query, show, isVisible]);
    const resultCount = shownGroups.reduce((acc, group) => acc + group.columns.length, 0);
    // Recuento sobre las columnas que lista el panel (fijas incluidas).
    const totals = groups.reduce((acc, group) => {
        const state = groupVisibility(group, isVisible);
        return { shown: acc.shown + state.shown, total: acc.total + state.total };
    }, { shown: 0, total: 0 });

    const toggleGroupOpen = (id) => setOpenGroups((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const firstToggleable = shownGroups.flatMap((group) => group.columns).find((column) => !column.locked);

    return (
        <SidePanel
            ariaLabel="Columnas de la hoja"
            title="Columnas"
            icon={<FiColumns />}
            subtitle={`${totals.shown} de ${totals.total} visibles`}
            onClose={onClose}
            searchValue={query}
            onSearchChange={setQuery}
            searchPlaceholder="Buscar columna…"
            onSearchEnter={() => { if (query.trim() && firstToggleable) onToggle(firstToggleable.id); }}
            toolbar={(
                <div className="obras-panel-segment" role="group" aria-label="Mostrar">
                    {SHOW_OPTIONS.map((option) => (
                        <button key={option.id} type="button" aria-pressed={show === option.id} className={show === option.id ? "is-active" : ""} onClick={() => setShow(option.id)}>
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
            footer={(
                <>
                    <button type="button" className="obras-panel-btn obras-panel-btn--ghost" onClick={onReset}><FiRefreshCw aria-hidden="true" /> Restablecer</button>
                    <button type="button" className="obras-panel-btn obras-panel-btn--ghost" onClick={onShowAll}>Mostrar todas</button>
                    <button type="button" className="obras-panel-btn obras-panel-btn--primary obras-panel-foot__done" onClick={onClose}>Listo</button>
                </>
            )}
        >
            {!filtering ? (
                <ViewsBar
                    views={views}
                    activeId={activeViewId}
                    onApply={onApplyView}
                    onDelete={onDeleteView}
                    onSave={onSaveView}
                    saveStatus={saveStatus}
                    maxNameLength={maxNameLength}
                />
            ) : (
                <p className="obras-panel-results">{resultCount} {resultCount === 1 ? "columna" : "columnas"}{query.trim() ? " · Intro marca/desmarca la primera" : ""}</p>
            )}

            {!shownGroups.length ? (
                <p className="obras-panel-empty">Ninguna columna coincide{query.trim() ? ` con «${query.trim()}»` : ""}.</p>
            ) : shownGroups.map((group) => {
                const fullGroup = groups.find((item) => item.id === group.id) || group;
                const state = groupVisibility(fullGroup, isVisible);
                const open = filtering || openGroups.has(group.id);
                return (
                    <section key={group.id} className={`obras-panel-group${open ? " is-open" : ""}`}>
                        <div className="obras-panel-group__head">
                            <button type="button" className="obras-panel-group__toggle" aria-expanded={open} onClick={() => toggleGroupOpen(group.id)} disabled={filtering}>
                                {filtering ? null : <FiChevronDown className="obras-panel-group__chevron" aria-hidden="true" />}
                                {group.icon ? React.createElement(group.icon, { className: "obras-panel-group__icon", "aria-hidden": true }) : null}
                                <span className="obras-panel-group__label">{group.label}</span>
                                <span className="obras-panel-group__count">{state.shown}/{state.total}</span>
                            </button>
                            {state.toggleableIds.length ? (
                                <button type="button" className="obras-panel-link" onClick={() => onSetMany(state.toggleableIds, !state.all)}>
                                    {state.all ? "Ocultar todas" : "Mostrar todas"}
                                </button>
                            ) : null}
                        </div>
                        {open ? (
                            <ul className="obras-panel-options">
                                {group.columns.map((column) => {
                                    const checked = column.locked || isVisible(column.id);
                                    return (
                                        <li key={column.id}>
                                            <label className={`obras-panel-option${checked ? " is-checked" : ""}${column.locked ? " is-locked" : ""}`} title={column.locked ? "Columna fija: da acceso a la ficha de la obra" : undefined}>
                                                <input type="checkbox" checked={checked} disabled={column.locked} onChange={() => onToggle(column.id)} />
                                                <span className="obras-panel-option__box" aria-hidden="true" />
                                                <span className="obras-panel-option__label"><Highlight text={column.label} query={query} /></span>
                                                {column.locked ? <FiLock className="obras-panel-option__lock" aria-label="Fija" /> : null}
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : null}
                    </section>
                );
            })}
        </SidePanel>
    );
}
